/**
 * Lightweight scroll parallax for media wrappers.
 *
 * On any [data-parallax] element, the inner <img> (or the element itself if it
 * is an <img>) is translated vertically as the element passes through the
 * viewport:
 *   data-parallax-speed="0.15"  — intensity (parseFloat, default 0.15)
 *
 * The wrapper is expected to clip overflow and the image is expected to be
 * slightly oversized (CSS .is-parallax) so the translate never reveals a gap.
 *
 * Entirely disabled when the user prefers reduced motion.
 */
(function () {
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;

  var items = [];
  var ticking = false;

  /* Viewport height is cached: on touch devices the URL bar collapsing fires
     resize events with a new innerHeight mid-scroll, and recomputing offsets
     from it makes parallax images jump. Only real size changes (width, i.e.
     rotation or a desktop resize) update it. */
  var isTouch = window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches;
  var viewportH = window.innerHeight || document.documentElement.clientHeight;
  var lastW = window.innerWidth;

  function findImage(el) {
    if (el.tagName === 'IMG') return el;
    return el.querySelector('img');
  }

  function init(el) {
    if (el.__parallaxInit) return;
    el.__parallaxInit = true;

    var img = findImage(el);
    if (!img) return;

    var speed = parseFloat(el.getAttribute('data-parallax-speed'));
    if (isNaN(speed)) speed = 0.2;

    /* Scale the image up ourselves so there is always room to translate
       without revealing an edge — independent of any CSS. Higher intensity
       means a bigger scale and therefore more visible travel. */
    var scale = 1 + Math.max(0.12, Math.min(0.5, speed * 1.4));
    img.style.willChange = 'transform';
    img.style.transformOrigin = 'center center';
    img.style.transform = 'translate3d(0, 0, 0) scale(' + scale + ')';

    items.push({ el: el, img: img, speed: speed, scale: scale });
  }

  function update() {
    ticking = false;

    var viewportCenter = viewportH / 2;

    for (var i = 0; i < items.length; i++) {
      var item = items[i];
      var rect = item.el.getBoundingClientRect();

      /* Skip elements far outside the viewport for performance */
      if (rect.bottom < -viewportH || rect.top > viewportH * 2) {
        continue;
      }

      var elementCenter = rect.top + rect.height / 2;
      var distance = viewportCenter - elementCenter;
      var offset = distance * item.speed;

      /* Clamp to the headroom created by our own scale() so the drift is as
         large as possible without ever revealing a gap. Derived from the known
         scale + element height, so it stays correct regardless of CSS. */
      var headroom = (item.scale - 1) / 2 * rect.height - 1;
      if (headroom < 0) headroom = 0;
      if (offset > headroom) offset = headroom;
      else if (offset < -headroom) offset = -headroom;

      item.img.style.transform = 'translate3d(0, ' + offset.toFixed(2) + 'px, 0) scale(' + item.scale + ')';
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  function onResize() {
    if (isTouch && window.innerWidth === lastW) return;
    lastW = window.innerWidth;
    viewportH = window.innerHeight || document.documentElement.clientHeight;
    onScroll();
  }

  function ready() {
    var els = document.querySelectorAll('[data-parallax]');
    Array.prototype.forEach.call(els, init);
    if (!items.length) return;

    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize, { passive: true });
  }

  if (document.readyState !== 'loading') {
    ready();
  } else {
    document.addEventListener('DOMContentLoaded', ready);
  }
})();
